import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useLanguage } from '../hooks/useLanguage';
import TopBar from './TopBar';
import ParcoursMapNeon from './ParcoursMapNeon';
import { ArrowRight, BarChart3, TrendingUp, Coins, Map } from 'lucide-react';

// Parser robusto para sim360_snapshot con fallbacks
const parseSnapshot = (snapshot) => {
  if (!snapshot) return null;
  
  try {
    const data = typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot;
    
    return {
      profile: data.profile || data.selectedProfile || 'Custom',
      allocations: {
        dca: data.allocations?.bitcoin || data.allocations?.dca_btc || 0,
        ai: data.allocations?.aiTrading || data.allocations?.ai || 0,
        web3: data.allocations?.web3 || 0,
        crypto: data.allocations?.diversified || data.allocations?.crypto_div || 0
      },
      aiSplit: data.aiSplit || data.ai_split || {},
      updatedAt: data.updatedAt || Date.now()
    };
  } catch (error) {
    console.error('Error parsing snapshot:', error);
    return null;
  }
};

const ParcoursOnboarding = () => {
  const { t, currentLang } = useLanguage();
  const [snapshot, setSnapshot] = useState(null);
  const [progress, setProgress] = useState(null);
  const [timelineVisible, setTimelineVisible] = useState([false, false, false]);

  useEffect(() => {
    // Load snapshot from localStorage
    const storedSnapshot = localStorage.getItem('sim360_snapshot');
    if (storedSnapshot) {
      const parsed = parseSnapshot(storedSnapshot);
      setSnapshot(parsed);
    }

    // Load progress from localStorage
    const storedProgress = localStorage.getItem('parcours_progress');
    if (storedProgress) {
      try {
        setProgress(JSON.parse(storedProgress));
      } catch (error) {
        console.error('Error parsing progress:', error);
      }
    }

    // Progressive timeline appearance
    const timer1 = setTimeout(() => setTimelineVisible([true, false, false]), 100);
    const timer2 = setTimeout(() => setTimelineVisible([true, true, false]), 350);
    const timer3 = setTimeout(() => setTimelineVisible([true, true, true]), 600);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  const [showMap, setShowMap] = useState(true);
  const [rocketLaunched, setRocketLaunched] = useState(false);

  const handleStart = () => {
    const newProgress = {
      currentStep: 'WEB3_1',
      completedSteps: [],
      startedAt: Date.now(),
      lastSeenAt: Date.now()
    };
    localStorage.setItem('parcours_progress', JSON.stringify(newProgress));
    setProgress(newProgress);
    setRocketLaunched(true);
    
    // TODO: Navigate to WEB3_1 step after animation
    setTimeout(() => {
      console.log('Starting parcours at WEB3_1');
      alert(t('parcoursStartAlert') || 'Navigation to Step 1 (Web3) will be implemented in next task');
    }, 1000);
  };

  const handleNodeClick = (nodeKey) => {
    console.log('Node clicked:', nodeKey);
    // Future: navigate to specific step
  };

  const handleResume = () => {
    if (progress?.currentStep) {
      console.log('Resuming at:', progress.currentStep);
      // TODO: Navigate to current step
      alert(`Resuming at ${progress.currentStep}`);
    }
  };

  const handleBackToSim = () => {
    window.location.href = `/?lang=${currentLang}`;
  };

  const timelineSteps = [
    {
      icon: Coins,
      label: t('parcoursTimelineWeb3'),
      color: 'from-[#00f4ff] to-[#12b7ff]',
      highlight: true
    },
    {
      icon: TrendingUp,
      label: t('parcoursTimelineAI'),
      color: 'from-[#12b7ff] to-[#0677aa]',
      highlight: false
    },
    {
      icon: BarChart3,
      label: t('parcoursTimelineDCA'),
      color: 'from-[#0677aa] to-[#00f4ff]',
      highlight: false
    }
  ];

  return (
    <div className="min-h-screen bg-[#07121E]">
      <TopBar />
      
      <div className="container mx-auto px-4 py-12 flex items-center justify-center min-h-[calc(100vh-80px)]">
        <Card className="max-w-2xl w-full">
          <CardContent className="p-8">
            {/* Title */}
            <h1 className="text-3xl font-bold text-center mb-4 bg-gradient-to-r from-[#00f4ff] to-[#12b7ff] bg-clip-text text-transparent">
              {t('parcoursTitle')}
            </h1>

            {/* Subtitle */}
            <p className="text-center text-[#9EB4C0] mb-8 leading-relaxed whitespace-pre-line">
              {t('parcoursSubtitle')}
            </p>

            {snapshot ? (
              <>
                {/* Parcours Map - Neon Premium Style */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Map className="w-4 h-4 text-[#00f4ff]" />
                    <span className="text-sm font-medium text-[#9EB4C0]">
                      {t('parcoursMapTitle') || 'Tu mapa de viaje'}
                    </span>
                  </div>
                  <ParcoursMapNeon 
                    activeNode={progress?.currentStep ? 
                      (progress.currentStep === 'WEB3_1' ? 'web3' : 
                       progress.currentStep === 'AI_1' ? 'ai' : 
                       progress.currentStep === 'DCA_1' ? 'dca' : 'start') 
                      : 'start'
                    }
                    progress={progress}
                    onNodeClick={handleNodeClick}
                    onRocketLaunch={() => setRocketLaunched(true)}
                    lang={currentLang}
                    reduceMotion={false}
                  />
                </div>

                {/* Profile Summary */}
                <div className="mb-6 p-4 rounded-lg bg-gradient-to-br from-[#0b1c38]/50 to-[#040816]/50 border border-[#00f4ff]/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-[#9EB4C0]">{t('parcoursProfileLabel')}:</span>
                    <Badge variant="outline" className="text-[#00f4ff] border-[#00f4ff]/40">
                      {snapshot.profile}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#9EB4C0]">{t('parcoursAllocationLabel')}:</span>
                    <span className="text-sm text-white font-medium">
                      DCA {snapshot.allocations.dcaCrypto ?? snapshot.allocations.dca ?? ((snapshot.allocations.bitcoin || 0) + (snapshot.allocations.diversified || 0))}% • IA {snapshot.allocations.aiTrading ?? snapshot.allocations.ai}% • Web3 {snapshot.allocations.web3}%
                    </span>
                  </div>
                </div>

                {/* Timeline */}
                <div className="space-y-4 mb-8">
                  {timelineSteps.map((step, index) => {
                    const IconComponent = step.icon;
                    return (
                      <div
                        key={index}
                        className={`flex items-center gap-4 p-4 rounded-xl transition-all duration-500 ${
                          timelineVisible[index] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                        } ${
                          step.highlight
                            ? 'bg-gradient-to-r from-[#00f4ff]/10 to-[#12b7ff]/10 border-2 border-[#00f4ff]/30 shadow-lg shadow-[#00f4ff]/20'
                            : 'bg-[#0b1c38]/30 border border-[#00f4ff]/10'
                        }`}
                      >
                        <div className={`p-3 rounded-lg bg-gradient-to-br ${step.color} flex-shrink-0`}>
                          <IconComponent className="w-5 h-5 text-[#040816]" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-base font-medium text-white">
                              {index + 1}️⃣ {step.label}
                            </span>
                            {step.highlight && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-[#00f4ff]/20 text-[#00f4ff]">
                                ⚡ Premier pas
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* CTAs */}
                <div className="space-y-4">
                  {/* Primary CTA */}
                  <Button
                    onClick={handleStart}
                    variant="default"
                    size="lg"
                    className="w-full bg-gradient-to-r from-[#00f4ff] to-[#12b7ff] text-[#040816] font-bold hover:shadow-xl hover:shadow-[#00f4ff]/40 transition-all duration-300"
                  >
                    {t('parcoursCTAStart')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  <p className="text-xs text-center text-[#9EB4C0]">
                    {t('parcoursCTAStartSubtext')}
                  </p>

                  {/* Secondary CTA */}
                  <Button
                    onClick={handleBackToSim}
                    variant="outline"
                    size="lg"
                    className="w-full border-[#00f4ff]/40 text-[#00f4ff] hover:bg-[#00f4ff]/10"
                  >
                    {t('parcoursCTABack')}
                  </Button>

                  {/* Resume link (only if progress exists) */}
                  {progress && (
                    <button
                      onClick={handleResume}
                      className="w-full text-center text-sm text-[#9EB4C0] hover:text-[#00f4ff] underline transition-colors"
                    >
                      {t('parcoursResume')}
                    </button>
                  )}
                </div>
              </>
            ) : (
              /* No snapshot fallback */
              <div className="text-center py-8">
                <p className="text-[#9EB4C0] mb-6">
                  {t('parcoursNoSnapshot')}
                </p>
                <Button
                  onClick={handleBackToSim}
                  variant="default"
                  size="lg"
                  className="bg-gradient-to-r from-[#00f4ff] to-[#12b7ff] text-[#040816] font-bold"
                >
                  {t('parcoursCTABack')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ParcoursOnboarding;
